import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { sliderImages } from '../data/artworkData';
import { kineticTypography } from '../utils/animations';
import { applySimpleAnimation } from '../utils/simpleAnimations';

const HeroSlider = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const navigate = useNavigate();
  const heroImageRef = useRef(null);
  const subtitleRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => {
      nextImage();
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Small delay to ensure DOM elements are ready
    const timer = setTimeout(() => {
      console.log('HeroSlider: Starting animations');

      // Try GSAP first, fallback to CSS animations
      try {
        // Typewriter effect for subtitle
        if (subtitleRef.current) {
          console.log('HeroSlider: subtitleRef found, starting typewriter effect');
          kineticTypography(subtitleRef, 'typewriter');
        }
      } catch (error) {
        console.log('GSAP animations failed, using CSS fallback:', error);

        // Fallback to simple CSS animations
        if (subtitleRef.current) {
          applySimpleAnimation(subtitleRef.current, 'slide-up');
        }
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % sliderImages.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + sliderImages.length) % sliderImages.length);
  };

  return (
    <div id="home" className="relative h-96 md:h-[500px] lg:h-[600px] w-full overflow-hidden">
      {/* Slider Images with Parallax */}
      {sliderImages.map((image, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${index === currentImage ? 'opacity-100' : 'opacity-0'
            }`}
        >
          <div className="absolute inset-0 bg-white">
            <img
              ref={index === currentImage ? heroImageRef : null}
              src={image}
              alt={`Ram Tirath Art Sculpture ${index + 1}`}
              className="w-full h-full object-contain scale-110"
              onError={(e) => {
                console.log(`Failed to load slider image: ${image}`);
                e.target.src = '/images/placeholder.jpg';
              }}
              onLoad={() => {
                console.log(`Successfully loaded slider image: ${image}`);
              }}
            />
          </div>
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        </div>
      ))}

      {/* Hero Content Overlay */}
      <div className="absolute inset-0 flex items-center justify-center text-white text-center z-10">
        <div className="max-w-4xl px-4">
          <p ref={subtitleRef} className="text-xl md:text-2xl lg:text-3xl mb-4 font-medium tracking-wide text-yellow-100 drop-shadow-lg">
            All Types of Sculptures & Custom Art Creations (Mural, Wallarts)
          </p>
          <p className="text-lg md:text-xl mb-8 text-orange-200 font-light tracking-wide">Leading Custom Sculpture Makers in India</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#contact"
              className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-lg font-semibold transition-all transform hover:scale-105"
            >
              Get Quote
            </a>
            <button
              onClick={() => navigate('/gallery')}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-8 py-3 rounded-lg font-semibold transition-all backdrop-blur-sm"
            >
              View Gallery
            </button>
          </div>
        </div>
      </div>

      {/* Navigation buttons */}
      <button
        onClick={prevImage}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-full transition-all z-20"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        onClick={nextImage}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-full transition-all z-20"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Dots indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
        {sliderImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentImage(index)}
            className={`w-3 h-3 rounded-full transition-all ${index === currentImage ? 'bg-white' : 'bg-white bg-opacity-50'
              }`}
          />
        ))}
      </div>
    </div>
  );
};

export default HeroSlider;
